import { makeAutoObservable } from 'mobx'
import { RootStore } from './RootStore'

export type GenerationType = 'ai' | 'default'

interface GenerationInfo {
  startedAt: string
  type: GenerationType
  message?: string
  replacingImageUrl?: string
  replaceFrameBatchId?: string
}

/**
 * Stores generation start timestamps for designs.
 * Used to persist elapsed time display across navigation.
 * Does NOT handle polling or generation completion detection.
 */
export class GenerationTimestampStore {
  rootStore: RootStore
  generations: Map<string, GenerationInfo> = new Map()

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  startGeneration(
    designKey: string,
    type: GenerationType = 'ai',
    message?: string,
    replacingImageUrl?: string,
    replaceFrameBatchId?: string,
  ) {
    this.generations.set(designKey, {
      startedAt: new Date().toISOString(),
      type,
      message,
      replacingImageUrl,
      replaceFrameBatchId,
    })
  }

  completeGeneration(designKey: string) {
    this.generations.delete(designKey)
  }

  getStartedAt(designKey: string): string | undefined {
    return this.generations.get(designKey)?.startedAt
  }

  getGenerationType(designKey: string): GenerationType | undefined {
    return this.generations.get(designKey)?.type
  }

  getMessage(designKey: string): string | undefined {
    return this.generations.get(designKey)?.message
  }

  getReplacingImageUrl(designKey: string): string | undefined {
    return this.generations.get(designKey)?.replacingImageUrl
  }

  getReplaceFrameBatchId(designKey: string): string | undefined {
    return this.generations.get(designKey)?.replaceFrameBatchId
  }
}
